// Phase 1C — fixed 500x500 world tiling with reorg-safe cache and race-safe
// paginated fetch. All tile logic lives here so it is unit-testable.
//
// Reviewer P0-2: paginated tile meta is a RANGE, not a scalar. For every page:
//   - chain is validated (delegated to api.viewport → validateChainMeta)
//   - as_of_block is recorded; min/max are aggregated across pages
//   - reconciled is conservative: false if ANY page is false
//   - dataStatus is conservative: degraded if ANY page is degraded
//   - a 200 page without a usable as_of_block fails CLOSED (INVALID_META)
// Reviewer P0-6: publication is separated; abort/error preserve prior snapshot.
import type { ViewerApi } from "./api";
import { ChainMismatchError, ViewerApiError } from "./api";
import type { TileFetchResult } from "./tile-reducer";
import type {
  DataStatus,
  Parcel,
  SnapshotRange,
  TileRecord,
  ViewportResponse,
} from "./types";

export const TILE_SIZE = 500;
export const PAGE_SIZE = 200;
export const WORLD = 1000;

export type TileCoord = { tileX: 0 | 1; tileY: 0 | 1 };

export function allTileCoords(): TileCoord[] {
  return [
    { tileX: 0, tileY: 0 },
    { tileX: 1, tileY: 0 },
    { tileX: 0, tileY: 1 },
    { tileX: 1, tileY: 1 },
  ];
}

export function tileKey(chainId: number, tileX: number, tileY: number): string {
  return `${chainId}:${tileX}:${tileY}`;
}

// Build a blank tile record for a coord.
export function emptyTile(tx: 0 | 1, ty: 0 | 1): TileRecord {
  return {
    tileX: tx,
    tileY: ty,
    x: tx * TILE_SIZE,
    y: ty * TILE_SIZE,
    w: TILE_SIZE,
    h: TILE_SIZE,
    state: "missing",
    parcels: [],
    hasSnapshot: false,
    asOfBlockMin: null,
    asOfBlockMax: null,
    reconciled: false,
    dataStatus: null,
    requestSeq: 0,
    fetchedAt: null,
    error: null,
    refreshError: null,
  };
}

// ---- Race-safe pagination ----
// Only return { kind: "ready", ... } when the ENTIRE paginated fetch succeeds
// AND every page validated the chain. Aborted mid-way → { kind: "aborted" }.
// Any HTTP / NETWORK / CHAIN_MISMATCH / INVALID_META → { kind: "error" }.
export async function fetchTileFull(
  api: ViewerApi,
  coord: TileCoord,
  signal: AbortSignal,
): Promise<TileFetchResult> {
  const acc: Parcel[] = [];
  let cursor: string | null = null;
  let asOfBlockMin: number | null = null;
  let asOfBlockMax: number | null = null;
  let reconciled = true; // AND over pages
  let dataStatus: DataStatus = "ok"; // degraded if ANY page is degraded
  let pages = 0;
  try {
    while (true) {
      const resp: ViewportResponse = await api.viewport(
        {
          x: coord.tileX * TILE_SIZE,
          y: coord.tileY * TILE_SIZE,
          w: TILE_SIZE,
          h: TILE_SIZE,
          cursor,
          page_size: PAGE_SIZE,
        },
        signal,
      );
      pages++;
      for (const p of resp.parcels) acc.push(p);

      const asOf = resp.meta.as_of_block;
      if (asOf == null || !Number.isFinite(asOf)) {
        return {
          kind: "error",
          error: "INVALID_META: viewport page missing as_of_block",
        };
      }
      if (asOfBlockMin == null || asOf < asOfBlockMin) asOfBlockMin = asOf;
      if (asOfBlockMax == null || asOf > asOfBlockMax) asOfBlockMax = asOf;
      // Conservative AND / degraded-OR aggregation.
      if (!resp.meta.reconciled) reconciled = false;
      if (resp.meta.data_status === "degraded") dataStatus = "degraded";

      if (!resp.next_cursor) break;
      cursor = resp.next_cursor;
      if (pages > 10_000) {
        return { kind: "error", error: "PAGINATION_RUNAWAY: too many pages" };
      }
    }
  } catch (err) {
    if (err instanceof ViewerApiError && err.aborted) {
      return { kind: "aborted" };
    }
    if (err instanceof ChainMismatchError) {
      return {
        kind: "error",
        error: `${err.code}: ${err.message}`,
      };
    }
    const msg =
      err instanceof ViewerApiError
        ? `${err.code}: ${err.message}`
        : (err as Error).message || String(err);
    return { kind: "error", error: msg };
  }
  return {
    kind: "ready",
    parcels: acc,
    asOfBlockMin,
    asOfBlockMax,
    reconciled,
    dataStatus,
    error: null,
  };
}

// ---- Global union / deduplication ----
// Uses hasSnapshot (not parcels.length) — a valid empty tile still participates.
// A parcel crossing tile boundaries is returned by up to 4 tiles; keep copy
// from the tile with the highest asOfBlockMax (§5).
export function unionParcels(tiles: TileRecord[]): Parcel[] {
  const byId = new Map<number, Parcel>();
  const seenBlock = new Map<number, number | null>();
  for (const t of tiles) {
    if (!t.hasSnapshot) continue;
    for (const p of t.parcels) {
      const prev = byId.get(p.id);
      if (!prev) {
        byId.set(p.id, p);
        seenBlock.set(p.id, t.asOfBlockMax);
        continue;
      }
      const prevBlock = seenBlock.get(p.id) ?? -1;
      const curBlock = t.asOfBlockMax ?? -1;
      if (curBlock > prevBlock) {
        byId.set(p.id, p);
        seenBlock.set(p.id, t.asOfBlockMax);
      }
    }
  }
  return Array.from(byId.values());
}

// Snapshot range (§6 + reviewer P0-2/P0-10):
// - includes drawable stale snapshots (not only state==="ready")
// - includes intra-tile skew (asOfBlockMin/Max within a single paginated tile)
// - "uniform" ⇒ zero skew both across AND within tiles
export function snapshotRange(tiles: TileRecord[]): SnapshotRange | null {
  const mins: number[] = [];
  const maxs: number[] = [];
  for (const t of tiles) {
    if (!t.hasSnapshot) continue;
    if (t.state !== "ready" && t.state !== "stale") continue;
    if (t.asOfBlockMin != null) mins.push(t.asOfBlockMin);
    if (t.asOfBlockMax != null) maxs.push(t.asOfBlockMax);
  }
  if (mins.length === 0 || maxs.length === 0) return null;
  const bmin = Math.min(...mins);
  const bmax = Math.max(...maxs);
  return { bmin, bmax, uniform: bmin === bmax };
}

// Stat-driven staleness: if stats.indexed_through_block > tile.asOfBlockMax,
// mark tile stale so a targeted refresh can pick it up.
export function markStaleAgainstStats(
  tiles: TileRecord[],
  statsBlock: number | null,
): TileRecord[] {
  if (statsBlock == null) return tiles;
  return tiles.map((t) => {
    if (
      t.hasSnapshot &&
      t.state === "ready" &&
      t.asOfBlockMax != null &&
      statsBlock > t.asOfBlockMax
    ) {
      return { ...t, state: "stale" };
    }
    return t;
  });
}
